// ============================================================
// ANSICHTSSACHE-AGENT – Gespeicherte Ansichten: welche Schluessel
// ============================================================
// Eine gespeicherte Ansicht ist der Suchteil der Adresse unter
// einem Namen. Nicht die GANZE Adresse: darin steht auch, was nur
// fuer diesen Augenblick gilt — das offene Blatt (`?umsatz=…`,
// `?person=…`), der Ausschnitt (`umfang`), die Seite einer
// Blaetterung. Wer das mitspeichert, oeffnet beim naechsten Mal
// einen Umsatz von letzter Woche.
//
// Deshalb eine Positivliste: gespeichert wird, was die Liste
// selbst als Filter nennt (die `params`, die auch „Filter
// zurücksetzen" leert), und dazu das, was die Liste FORMT, ohne
// zu filtern — Gruppierung, Sortierung, Zeitachse. Was hier
// fehlt, geht beim Speichern verloren; was zu viel drin waere,
// kaeme ungefragt wieder. Das Erste ist das kleinere Uebel.
//
// Rein und ohne Datenbank: der Browser rechnet damit, ob die
// Adresse noch der gewaehlten Ansicht entspricht.
// ============================================================

/** Formt die Liste, ohne zu filtern — steht in keiner `params`-Liste. */
export const SHAPE_KEYS = [
  "gruppe",
  "sortierung",
  "sort",
  "richtung",
  // Die Sortierrichtung in Banking, wo `richtung` Eingang/Ausgang
  // filtert (`SortParams` in `lib/sorting.ts`).
  "sortierrichtung",
  "stufe",
  "am",
  // Das Ende eines freien Zeitraums (`stufe=frei`, `lib/zeitachse.ts`)
  "bis",
  // Board, Kalender, Liste — wie die Arbeit dargestellt ist
  "darstellung",
  "zeitraum",
  // Nicht `gross`: im DAM ist das die offene Lightbox, also ein
  // Blatt wie `datei` — gespeichert ginge die Datei jedes Mal
  // wieder auf (bis 15.10.2026 stand es hier).
] as const;

/** Gilt nur fuer den Augenblick und wird nie gespeichert. */
export const TRANSIENT_KEYS = ["umfang", "seite", "page"] as const;

/**
 * Der Schluessel, unter dem die gewaehlte Ansicht in der Adresse
 * steht. Er bleibt stehen, waehrend jemand weiterfiltert — daran
 * erkennt der Kopf „Meine offenen · geändert".
 */
export const VIEW_PARAM = "ansicht";

/** Welche Schluessel eine Ansicht dieser Liste ausmachen. */
export function viewKeys(filterParams: readonly string[]): Set<string> {
  const keys = new Set<string>([...filterParams, ...SHAPE_KEYS]);
  for (const k of TRANSIENT_KEYS) keys.delete(k);
  keys.delete(VIEW_PARAM);
  return keys;
}

/**
 * Der Teil der Adresse, der eine Ansicht ausmacht — sortiert,
 * damit dieselbe Auswahl immer dieselbe Zeichenkette ergibt,
 * egal in welcher Reihenfolge die Filter gesetzt wurden.
 */
export function viewQuery(search: URLSearchParams | string, keys: Set<string>): string {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const pairs: [string, string][] = [];
  for (const [key, value] of params) {
    if (keys.has(key) && value !== "") pairs.push([key, value]);
  }
  pairs.sort(([a, av], [b, bv]) => (a === b ? av.localeCompare(bv) : a.localeCompare(b)));
  return new URLSearchParams(pairs).toString();
}

/** Dieselbe Ansicht, auch wenn die Reihenfolge eine andere ist. */
export function sameView(a: string, b: string, keys: Set<string>): boolean {
  return viewQuery(a, keys) === viewQuery(b, keys);
}

/** Ob in der Adresse ueberhaupt etwas steht, das eine Ansicht ausmacht. */
export function hasViewState(search: URLSearchParams, keys: Set<string>): boolean {
  return viewQuery(search, keys) !== "";
}

/**
 * Die Adresse nach dem Wechsel auf eine Ansicht.
 *
 * Ersetzt wird nur, was eine Ansicht ausmacht — ein offenes
 * Blatt bleibt offen, so wie beim Umstellen eines einzelnen
 * Filters auch. Der Ausschnitt faellt weg: eine neue Auswahl
 * beginnt mit einem neuen Sichtfenster.
 */
export function applyView(
  search: URLSearchParams | string,
  keys: Set<string>,
  view: { id: string; query: string } | null,
): string {
  const next = new URLSearchParams(typeof search === "string" ? search : search.toString());
  for (const key of [...next.keys()]) {
    if (keys.has(key) || key === VIEW_PARAM || (TRANSIENT_KEYS as readonly string[]).includes(key))
      next.delete(key);
  }
  if (view) {
    for (const [key, value] of new URLSearchParams(view.query)) {
      if (keys.has(key)) next.append(key, value);
    }
    next.set(VIEW_PARAM, view.id);
  }
  return next.toString();
}

/** Laengste zulaessige Bezeichnung — sie steht im Kopf neben dem Zuruecksetzen. */
export const VIEW_NAME_MAX = 60;
